import React, { FC } from 'react';
import { mainColors } from '../../../../../styling/theme';
import { CustomColumn } from '../../../../../types/components/tables/tableTypes';
import { Status } from '../../../../../types/redux/data/dataTypes';
import type { CollateralStressTestPayload } from '../../../../../api/rs-hg/risk/collateralStressTest.types';
import { percentageToTwoDecimalPlaces } from '../../../../../utilities/numberFormatters';
import GridItem from '../../../../layout/GridComponents/GridItem';
import CustomTable from '../../../../tables/CustomTable';
import GenericStatusCell from '../../../../tables/GenericStatusCell';
import { specifyStatus } from '../../../../../utilities/generalMappings';

interface HairCutTableProps {
  payload: CollateralStressTestPayload;
}

interface HairCutTableData {
  haircut: string;
  stressedPL: number;
  hairCutLimit: number;
  status: Status;
}

function buildHairCutTableData(
  payload: CollateralStressTestPayload,
): HairCutTableData[] {
  const positions = payload.positions;
  if (typeof positions === 'string') return [];

  const haircut = 'Haircut Stress Test';
  const stressedPL = payload.stressed_pnl;
  const hairCutLimit = payload.haircut_limit;
  const status = specifyStatus(payload.status);
  return [
    {
      haircut,
      stressedPL,
      hairCutLimit,
      status,
    },
  ];
}

const tableColumns: CustomColumn<HairCutTableData>[] = [
  {
    title: 'Haircut',
    field: 'haircut',
    headerStyle: {
      textAlign: 'center',
    },
    cellStyle: {
      padding: 0,
      fontWeight: 700,
      color: mainColors.mainBlue,
      textAlign: 'center',
    },
  },
  {
    title: 'Stressed P/L',
    field: 'stressedPL',
    headerStyle: {
      textAlign: 'center',
    },
    cellStyle: {
      textAlign: 'center',
      padding: 0,
    },
    render: (rowData: HairCutTableData) =>
      percentageToTwoDecimalPlaces(rowData.stressedPL),
  },
  {
    title: 'Haircut Limit',
    field: 'hairCutLimit',
    headerStyle: {
      textAlign: 'center',
    },
    cellStyle: {
      textAlign: 'center',
      padding: 0,
    },
    render: (rowData: HairCutTableData) =>
      percentageToTwoDecimalPlaces(rowData.hairCutLimit),
  },
  {
    title: 'Status',
    field: 'status',
    headerStyle: {
      textAlign: 'center',
    },
    cellStyle: {
      textAlign: 'center',
      padding: 0,
    },
    pdfRenderType: 'PageLinkButton',
    render: (rowData: HairCutTableData) => (
      <GenericStatusCell
        height={'3.12rem'}
        status={rowData.status ? rowData.status : specifyStatus('Pass')}
      />
    ),
  },
];

const HairCutTable: FC<HairCutTableProps> = (props) => {
  const tableData = buildHairCutTableData(props.payload);
  return tableData.length ? (
    <GridItem card xs={12}>
      <CustomTable<HairCutTableData>
        pdfNoClearFirstRow
        options={{
          paging: false,
          exportButton: true,
        }}
        showToolbar={true}
        data={tableData}
        columns={tableColumns}
      />
    </GridItem>
  ) : null;
};

export default HairCutTable;
